<template>
  <div>
    <div v-if="!isAuthenticated">
      <MessageCard
        :displayMessage="displayMessage"
        :buttonText="'Login'"
        :buttonRoute="'/login'"
      />
    </div>

    <div v-else-if="!hasPackage">
      <MessageCard
        :displayMessage="displayMessage"
        :buttonText="'Subscribe Now'"
        :buttonRoute="'/payment'"
      />
    </div>

    <div v-else-if="hasFeature && !isExpired">
      <slot name="authorized-content"></slot>
    </div>

    <div v-else-if="isExpired">
      <MessageCard
        :displayMessage="displayMessage"
        :buttonText="'Subscribe Now'"
        :buttonRoute="'/payment'"
      />
    </div>

    <div v-else>
      <MessageCard
        :displayMessage="displayMessage"
        :buttonText="'Subscribe Now'"
        :buttonRoute="'/payment'"
      />
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, computed } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import MessageCard from "src/components/MessageCard.vue";
import useAddOns from "./AddOns.js";

const props = defineProps({
  packageAuthorization: {
    type: String,
    default: "Basic",
  },
  accessToAuthUser: {
    type: Boolean,
    default: false,
  },
});

const { mapAddOns } = useAddOns();
const $store = useStore();
const routeName = computed(() => useRoute()?.path);

const isAuthenticated = ref(false);
const hasPackage = ref(true);
const hasFeature = ref(true);
const isExpired = ref(false);
const displayMessage = ref("");

onMounted(() => {
  getPackageStatus();
});

const getPackageStatus = async () => {
  const { authenticate, packages } = $store.state;
  isAuthenticated.value = authenticate.isAuthenticated;

  if (!isAuthenticated.value) {
    displayMessage.value =
      "This feature is only available for logged-in users. Please log in to access it.";
    return;
  }

  if (props.accessToAuthUser) {
    hasFeature.value = true;
    isExpired.value = false;
    hasPackage.value = true;
    return;
  }

  if (packages.packageStatus?.current_package) {
    getPackageDetails(packages);
  } else {
    await $store.dispatch("packages/getPackageStatus");
    getPackageDetails($store.state.packages);
  }
};

const getPackageDetails = (packages) => {
  const currentPackage = packages.packageStatus?.current_package;
  const allPackagesFeaturesList = packages.packageStatus?.package_features;
  const userCurrentPackageFeatures = currentPackage?.features;
  hasPackage.value = !!currentPackage;
  isExpired.value = currentPackage?.expired_status;
  if (isExpired.value) {
    isExpired.value = !dailySignalStatus();
  }

  if (extraPermissions()) {
    hasPackage.value = true;
    isExpired.value = false;
  }

  if (!hasPackage.value) {
    displayMessage.value =
      "This feature is only available for paid subscribers. Please subscribe to a package.";
    return;
  }

  if (isExpired.value) {
    displayMessage.value =
      "Your daily signals have expired. Please contact support for an extension.";
    return;
  }

  const specificPropsPackageFeatures = getPropsPackageFeatures(
    allPackagesFeaturesList,
    props.packageAuthorization
  );
  if (extraPermissions()) {
    hasFeature.value = true;
  } else {
    hasFeature.value = checkHasFeature(
      hasUserSepcificFeature(
        specificPropsPackageFeatures,
        userCurrentPackageFeatures
      )
    );
  }

  if (!hasFeature.value) {
    displayMessage.value = noAccessMessage[props.packageAuthorization] || "";
  }
};

const getPropsPackageFeatures = (
  allPackagesFeaturesList,
  packageAuthorization
) => {
  const packageHierarchy = {
    Platinum: "Diamond+",
    "Diamond+": "Diamond",
    "Diamond+": "Gold",
    Diamond: "Gold",
    Gold: "Silver",
    Silver: "Basic",
  };

  const nextLowerPackage = packageHierarchy[packageAuthorization];
  const lowerPackageFeatures = nextLowerPackage
    ? allPackagesFeaturesList[nextLowerPackage]
    : [];

  return (allPackagesFeaturesList[packageAuthorization] || []).filter(
    (item) => !lowerPackageFeatures.includes(item)
  );
};

const checkHasFeature = (hasUserSepcificFeature) => {
  return hasUserSepcificFeature || dailySignalStatus() || extraAddOns();
};

const hasUserSepcificFeature = (
  specificPropsPackageFeatures,
  userCurrentPackageFeatures
) => {
  return userCurrentPackageFeatures?.some((feature) =>
    specificPropsPackageFeatures.includes(feature)
  );
};

const dailySignalStatus = () => {
  const { packages } = $store.state;
  return !packages.packageStatus?.current_package?.daily_signals_expired_status;
};

const extraAddOns = () => {
  const { packages } = $store.state;
  return packages.packageStatus?.extra_addon?.includes(
    mapAddOns(routeName.value)
  );
};

const extraPermissions = () => {
  const { packages } = $store.state;
  return packages.packageStatus?.extra_permissions?.includes(
    mapAddOns(routeName.value)
  );
};

const noAccessMessage = {
  Silver:
    "This feature is exclusive to Silver and higher-tier packages. Kindly subscribe to access it.",
  Gold: "This feature is exclusive to Gold and higher-tier packages. Kindly subscribe to access it.",
  Diamond:
    "This feature is exclusive to Diamond and higher-tier packages. Kindly subscribe to access it.",
  "Diamond+":
    "This feature is exclusive to Diamond Plus and higher-tier packages. Kindly subscribe to access it.",
  Platinum:
    "This feature is exclusive to Platinum packages. Kindly subscribe to access it.",
};
</script>
